// ============================================
// FRONTEND: hooks/useFeedBatches.js
// Rolling Window Infinite Scroll Hook with Client-Side Cache
// FIXED: Focus preservation by event ID + two-way scroll adjustment
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../utils/axiosConfig';
import normalizeEvent from '../utils/normalizeEvent';
import EventService from '../services/eventService';

// INSTAGRAM-STYLE: Larger buffers for seamless infinite scroll
// Backend over-fetches (returns up to 30 items per request), so we increase batch size to match
const BUFFER_SIZE = 90;            // Total items in memory (1.5x larger - can hold 3 full batches)
const WINDOW_SIZE = 30;            // Items actively rendered (unchanged - good balance)
const BATCH_SIZE = 15;             // Load 15 items at a time (50% more - matches backend better)
const TRIGGER_THRESHOLD_DOWN = 23; // Load next when at index 23 (7 from bottom of 30-item window)
const TRIGGER_THRESHOLD_UP = 7;    // Load previous when at index 7 (7 from top)
const TRIM_THRESHOLD = 120;        // Trim cache when buffer exceeds 120 items (increased)
const TRIM_AMOUNT = 30;            // Remove 30 items when trimming (1 full batch)

/**
 * Rolling Window Infinite Scroll Hook with Client-Side Cache
 *
 * Maintains a fixed window of 15 events, loading 5 at a time bidirectionally
 * Uses client-side cache since the backend API only supports forward pagination
 *
 * @param {Object} config - Configuration
 * @param {string} config.userId - User ID for personalization
 * @param {string} config.language - Language preference
 * @param {number} config.lat - User latitude
 * @param {number} config.lng - User longitude
 * @param {boolean} config.enabled - Enable/disable the hook
 */
export const useFeedBatches = ({
  userId = null,
  language = 'EN',
  lat = null,
  lng = null,
  enabled = true,
  searchQuery = '',   // NEW: optional text search
  category = null,    // NEW: optional category filter
  startDate = null,   // NEW: optional start date filter (YYYY-MM-DD)
  endDate = null,     // NEW: optional end date filter (YYYY-MM-DD)
  source = ['events'],        // NEW: Which collections to search
  excludeCategories = [],     // NEW: Categories to exclude
  radiusKm = 50       // NEW: Radius in km for geo filter
} = {}) => {
  // State
  const [items, setItems] = useState([]);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [focusIndex, setFocusIndex] = useState(7); // Center item (index 7 of 15)
  const [focusIsAuthoritative, setFocusIsAuthoritative] = useState(true); // NEW: Whether focus was truly preserved
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isShiftingWindow, setIsShiftingWindow] = useState(false); // NEW: Prevent interference during shifts
  const [pendingScrollAdjustment, setPendingScrollAdjustment] = useState(null); // NEW: Two-way scroll compensation

  // Client-side cache for all loaded events
  const eventCacheRef = useRef([]); // Stores all events loaded so far
  const windowStartIndexRef = useRef(0); // Index in cache where current window starts

  // Refs for API cursor and deduplication
  const nextCursorRef = useRef(null);
  const loadingLockRef = useRef(false);
  const focusedEventIdRef = useRef(null);
  const focusedIndexRef = useRef(7); // Track current focus index for math-based preservation
  const currentFocusEventIdRef = useRef(null); // TASK 1: Track event ID for focus preservation
  const focusIsAuthoritativeRef = useRef(true); // NEW: Track if current focus is authoritative
  const seenIdsRef = useRef(new Set()); // Track seen event IDs
  const recentlySeenIdsRef = useRef(new Set()); // Track recently seen IDs (last 50)
  const scrollAdjustmentIdRef = useRef(0); // Unique ID for each adjustment
  /**
   * Hard circuit breaker against runaway window shifting.
   *
   * `loadingLockRef` only prevents *concurrent* shifts; it is released the
   * moment one finishes, so anything that re-requests a shift on the very next
   * frame could chain indefinitely. Every completed shift now blocks further
   * shifts for a short settling period, giving the list time to apply scroll
   * compensation and the user time to actually move. This is a backstop: it
   * bounds the damage of any future trigger that forgets to gate itself.
   */
  const shiftCooldownUntilRef = useRef(0);
  const SHIFT_COOLDOWN_MS = 600;
  const lastShiftPreservedByEventIdRef = useRef(false); // Part 3: Track if last shift preserved by event ID
  const wrapCycleCountRef = useRef(0); // Track how many times we've wrapped/refreshed
  const isInRotationModeRef = useRef(false); // Track if we're rotating (true) or still loading new content (false)
  const recycleCycleRef = useRef(0);  // How many times we've replayed the cache
  const recycleOffsetRef = useRef(0); // Where in the cache the next replay resumes
  const renderKeySeqRef = useRef(0);  // Monotonic counter for unique __renderKeys

  /**
   * Stamp a globally unique `__renderKey` on every item entering the cache.
   *
   * BUGFIX (list renders fewer rows than the window contains): `__renderKey`
   * used to be assigned only by appendRecycledBatch, so ordinary fetched items
   * had none and fell back to `event_id` everywhere it was consumed. That was
   * fine right up until a fetch path deliberately admitted duplicate ids — the
   * "final retry" in loadNext appends its results with no dedup filter at all,
   * because when the pool is dry that is the only way to get any content.
   *
   * Two things then broke, both silently:
   *   1. SmartEventList dedups displayItems on `__renderKey || event_id`, so
   *      those duplicates were filtered straight back out. The hook reported a
   *      16-item window while the list rendered 8 rows — a second, independent
   *      halving of the scrollable height on top of the window shrink.
   *   2. SimpleScrollList anchors scroll compensation on `data-render-key` and
   *      takes the FIRST matching element. With several rows sharing one id it
   *      could measure drift against a different copy far up the list, which is
   *      where the bogus `drift` values in the trace came from.
   *
   * Keys are per-copy, not per-event: dedup against the backend still happens
   * on `event_id`, saving/view-counts/map markers still resolve the real record.
   * This only gives every *rendered copy* an identity of its own.
   */
  const withRenderKeys = useCallback((events) => {
    if (!Array.isArray(events)) return events;
    return events.map((event) => {
      if (event && event.__renderKey) return event; // already identified
      const baseId = String(event?.event_id ?? event?.id ?? 'item');
      renderKeySeqRef.current += 1;
      return { ...event, __renderKey: `${baseId}__k${renderKeySeqRef.current}` };
    });
  }, []);

  /**
   * Hydrate events with PostgreSQL images by batch fetching
   */
  const hydrateEventsWithImages = useCallback(async (events) => {
    if (!events || events.length === 0) return events;

    try {
      // Split events and reviews - only hydrate events from PostgreSQL
      const eventItems = events.filter(e => e._source !== 'review');
      const reviewItems = events.filter(e => e._source === 'review');

      // Extract event IDs (skip reviews)
      // FIX: only send numeric IDs — the feed also contains venues (_source: 'venue')
      // whose UUID ids the /events/batch endpoint rejects; a page with zero numeric
      // IDs previously caused a 400 Bad Request on every hydration call.
      const eventIds = eventItems
        .map(e => e.event_id || e.id)
        .filter(Boolean)
        .filter(id => /^\d+$/.test(String(id)));

      if (eventIds.length === 0) {
        // Nothing hydratable (only reviews/venues), no hydration needed
        return events.map(e => ({ ...e, _imageSource: e._source === 'review' ? 'review' : 'typesense' }));
      }

      console.log('🖼️ [useFeedBatches] Hydrating images for', eventIds.length, 'events from PostgreSQL (skipping', reviewItems.length, 'reviews)');

      // Batch fetch full event data from PostgreSQL
      const pgEvents = await EventService.getBatchEvents(eventIds);

      if (!pgEvents || pgEvents.length === 0) {
        console.warn('⚠️ [useFeedBatches] No events returned from PostgreSQL batch fetch');
        return events;
      }

      // Create a map of event_id -> PostgreSQL fields needed by map/list UI.
      // The feed may start from Typesense-only docs, so keep creator metadata
      // from the canonical events batch response as well as image fields.
      const eventDataMap = new Map();
      pgEvents.forEach(pgEvent => {
        const id = String(pgEvent.event_id || pgEvent.id);
        if (id) {
          eventDataMap.set(id, pgEvent);
        }
      });

      console.log('🎨 [useFeedBatches] Created PostgreSQL event map with', eventDataMap.size, 'entries');

      // Merge PostgreSQL images back into events; pass reviews through as-is
      const hydratedEvents = events.map(event => {
        // Reviews already have their image from Typesense/backend normalization
        if (event._source === 'review') {
          return { ...event, _imageSource: 'review' };
        }

        const eventId = String(event.event_id || event.id);
        const pgEvent = eventDataMap.get(eventId);
        const pgImage = pgEvent?.event_image_link || pgEvent?.image_url || pgEvent?.poster_url;

        if (pgEvent) {
          const additionalImages = Array.isArray(pgEvent.additional_image_links) && pgEvent.additional_image_links.length > 0
            ? pgEvent.additional_image_links
            : event.additional_image_links ?? [];

          return {
            ...event,
            event_creator_id: pgEvent.event_creator_id ?? event.event_creator_id,
            creator_name: pgEvent.creator_name ?? event.creator_name,
            user_name: pgEvent.user_name ?? event.user_name,
            event_image_link: pgImage || event.event_image_link,
            image_url: pgImage || event.image_url,
            additional_image_links: additionalImages,
            _imageSource: pgImage ? 'postgresql' : 'typesense'
          };
        }

        // Keep original if no PostgreSQL image found
        return {
          ...event,
          _imageSource: 'typesense' // Debug flag
        };
      });

      // Log hydration results
      const hydratedCount = hydratedEvents.filter(e => e._imageSource === 'postgresql').length;
      console.log('✅ [useFeedBatches] Hydrated', hydratedCount, '/', events.length, 'images from PostgreSQL');

      return hydratedEvents;
    } catch (error) {
      console.error('❌ [useFeedBatches] Image hydration failed:', error);
      // Return original events on error
      return events;
    }
  }, []);

  /**
   * Fetch events from API using POST /events/search/feed
   * Then hydrate with PostgreSQL images
   * INFINITE SCROLL: Manages wrap_refresh cycles to never run out of content
   * @param {string|null} cursor - Pagination cursor
   * @param {number} limit - Number of items to fetch
   * @param {boolean} skipExclusions - If true, don't send exclude_ids (used during prefetch)
   */
  const fetchEvents = useCallback(async (cursor = null, limit = BATCH_SIZE, skipExclusions = false) => {
    try {
      // INFINITE SCROLL: Handle wrap_refresh cursor with smart rotation
      let actualCursor = cursor;
      let excludeIds = [];

      // Skip exclusions during prefetch OR when loading new content from database
      if (skipExclusions || !isInRotationModeRef.current) {
        if (skipExclusions) {
          console.log('📋 [useFeedBatches] Prefetch mode - fetching without exclusions');
        } else {
          console.log('📋 [useFeedBatches] Loading new content - minimal exclusions (last 10 items only)');
          // Still exclude the very last items to avoid immediate repeats
          const recentArray = Array.from(recentlySeenIdsRef.current);
          excludeIds = recentArray.slice(-10);
        }
      } else if (cursor === 'wrap_refresh') {
        console.log('🔄 [useFeedBatches] Wrap refresh detected - using smart rotation strategy');
        actualCursor = null; // Reset to page 1
        wrapCycleCountRef.current += 1;

        // INSTAGRAM-STYLE ROTATION: For small pools, use very aggressive exclusion reduction
        const allRecentlySeen = Array.from(recentlySeenIdsRef.current);
        const cacheSize = eventCacheRef.current.length;

        console.log('📋 [useFeedBatches] Wrap cycle #' + wrapCycleCountRef.current + ' - Recently seen:', allRecentlySeen.length, 'items, cache:', cacheSize);

        // CRITICAL FIX: For small pools, exclude only the LAST few items (not a ratio)
        // This ensures we get fresh items even with pools of 25-50 items
        if (wrapCycleCountRef.current === 1) {
          // First wrap: Exclude only last 5 items (maximum freshness)
          excludeIds = allRecentlySeen.slice(-Math.min(5, allRecentlySeen.length));
          console.log('📋 [useFeedBatches] First wrap - excluding only last', excludeIds.length, 'items for maximum variety');
        } else if (wrapCycleCountRef.current % 3 === 0) {
          // Every 3rd wrap: Clear most exclusions for full refresh
          excludeIds = [];
          recentlySeenIdsRef.current = new Set();
          console.log('📋 [useFeedBatches] Periodic full refresh - cleared all exclusions');
        } else {
          // Other wraps: Exclude last 10 items
          excludeIds = allRecentlySeen.slice(-Math.min(10, allRecentlySeen.length));
          console.log('📋 [useFeedBatches] Subsequent wrap - excluding last', excludeIds.length, 'items');
        }
      } else {
        // Normal fetch - exclude recently seen items
        excludeIds = Array.from(recentlySeenIdsRef.current);
      }

      // Build request body for POST /events/search/feed
      const requestBody = {
        limit: limit,
        cursor: actualCursor,
        allow_langs: language ? [language] : ['TH', 'EN'],
        userId: userId || null,
        exclude_ids: excludeIds,
      };

      // NEW: optional text search using same field name as Typesense
      if (searchQuery && searchQuery.trim()) {
        requestBody.q = searchQuery.trim();
      }

      // NEW: optional category filter
      if (category && category.trim()) {
        requestBody.category = category.trim();
      }

      // NEW: optional date range filter (YYYY-MM-DD format)
      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }

      // NEW: source and excludeCategories for multi-collection search
      if (source && Array.isArray(source)) {
        requestBody.source = source;
      }
      if (excludeCategories && Array.isArray(excludeCategories) && excludeCategories.length > 0) {
        requestBody.excludeCategories = excludeCategories;
      }

      // Add geo filter if location is provided
      if (lat && lng) {
        requestBody.geo = {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          radiusKm: parseFloat(radiusKm)
        };
      }

      console.log('[useFeedBatches] Fetching with cursor:', cursor, 'limit:', limit);
      console.log('🔍 [useFeedBatches] Request body:', JSON.stringify(requestBody, null, 2));

      const response = await api.post('/events/search/feed', requestBody);

      if (response.data && Array.isArray(response.data.items)) {
        console.log('📦 [useFeedBatches] Received', response.data.items.length, 'raw items from Typesense');

        // Normalize events first
        const normalizedEvents = response.data.items.map(normalizeEvent);

        // Hydrate with PostgreSQL images
        // Single choke point for render keys: every item that can reach the
        // cache passes through here (initialize, prefetch, normal loadNext,
        // wrap refresh, and the unfiltered final retry).
        const hydratedEvents = withRenderKeys(await hydrateEventsWithImages(normalizedEvents));

        // Log first item AFTER hydration
        if (hydratedEvents.length > 0) {
          console.log('✨ [useFeedBatches] First item after hydration:', {
            id: hydratedEvents[0].event_id,
            name: hydratedEvents[0].event_name?.substring(0, 30),
            image: hydratedEvents[0].event_image_link?.substring(0, 60),
            source: hydratedEvents[0]._imageSource,
            // Check category fields for map marker icons
            type: hydratedEvents[0].type,
            main_category: hydratedEvents[0].main_category,
            category: hydratedEvents[0].category
          });
        }

        // Add to seen IDs
        // During prefetch (skipExclusions=true), only track in full history, not in recent
        // This allows all prefetched items to appear in rotation
        hydratedEvents.forEach(event => {
          const eventId = String(event.event_id || event.id);
          if (eventId) {
            seenIdsRef.current.add(eventId); // Full history

            // Only add to recently seen if not in prefetch mode
            if (!skipExclusions) {
              recentlySeenIdsRef.current.add(eventId); // Recent window for deduplication
            }
          }
        });

        // Trim recently seen if it gets too large (keep last 100)
        if (!skipExclusions && recentlySeenIdsRef.current.size > 100) {
          const recentArray = Array.from(recentlySeenIdsRef.current);
          recentlySeenIdsRef.current = new Set(recentArray.slice(-100));
          console.log('🗑️ [useFeedBatches] Trimmed recently seen to 100 items');
        }

        // INFINITE SCROLL: Handle wrap_cycle from server
        // Only wrap when server explicitly signals wrap_cycle AND we have no more pages
        let finalNextCursor = response.data.next_cursor;

        if (response.data.wrap_cycle > 0) {
          console.log('🔄 [useFeedBatches] Server signaled wrap_cycle:', response.data.wrap_cycle);

          // If we got items, continue with the provided cursor (might have more pages)
          if (hydratedEvents.length > 0 && response.data.next_cursor) {
            console.log('📄 [useFeedBatches] Got', hydratedEvents.length, 'items with wrap signal - will continue to next page:', response.data.next_cursor);
            finalNextCursor = response.data.next_cursor;
          }
          // If no items AND no cursor, truly exhausted - time to wrap
          else if (hydratedEvents.length === 0) {
            console.log('🔄 [useFeedBatches] Pool exhausted (0 items, no cursor) - entering rotation mode');
            isInRotationModeRef.current = true; // Switch to rotation mode
            finalNextCursor = 'wrap_refresh';
          }
        }

        return {
          events: hydratedEvents,
          nextCursor: finalNextCursor,
          hasMore: !!finalNextCursor
        };
      }

      return { events: [], nextCursor: null, hasMore: false };
    } catch (err) {
      console.error('[useFeedBatches] Fetch error:', err);
      throw err;
    }
  }, [language, userId, lat, lng, searchQuery, category, startDate, endDate, source, excludeCategories, hydrateEventsWithImages, withRenderKeys]);

  /**
   * Update the visible window from cache
   * TASK 1: Enhanced to support event-ID-based preservation first, then index-based fallback
   */
  const updateWindowFromCache = useCallback((startIndex, preserveFocusIndex = null, preserveFocusEventId = null) => {
    const cache = eventCacheRef.current;

    /**
     * BUGFIX (vertical list stalls at the bottom): the upper bound used to be
     * `cache.length - 1`, so as soon as `startIndex + WINDOW_SIZE` ran past the
     * end of the cache the window was *truncated* rather than slid back — the
     * clamp on `windowEnd` below silently ate the difference.
     *
     * That made the window ratchet smaller on every shift and never recover
     * (30 → 16 → 9 in the trace), because `loadNext` advances the start by
     * BATCH_SIZE unconditionally while a dry backend only adds ~8 items per
     * cycle. A shrinking window means shrinking content height, and "the list
     * won't scroll any further" is exactly what a collapsed scrollable height
     * looks like from the outside.
     *
     * The window is now a CONSTANT WINDOW_SIZE whenever the cache can supply
     * that many items: the start slides back to make room instead of the end
     * being clipped. Callers must therefore grow the cache *before* asking for
     * a start this can't honour — see loadNext.
     */
    const maxStart = Math.max(0, cache.length - WINDOW_SIZE);
    const windowStart = Math.max(0, Math.min(startIndex, maxStart));
    const windowEnd = Math.min(windowStart + WINDOW_SIZE, cache.length);
    const windowItems = cache.slice(windowStart, windowEnd);

    console.log('[useFeedBatches] 📊 Window calculation:', {
      requestedStart: startIndex,
      actualStart: windowStart,
      clampedBy: startIndex > maxStart ? startIndex - maxStart : 0,
      cacheLength: cache.length,
      windowSize: WINDOW_SIZE,
      itemsInWindow: windowItems.length
    });

    // Should be unreachable now that the start slides back; if it ever fires,
    // the cache genuinely holds fewer than WINDOW_SIZE items.
    if (windowItems.length < WINDOW_SIZE && cache.length >= WINDOW_SIZE) {
      console.warn('[useFeedBatches] ⚠️ Window smaller than WINDOW_SIZE despite a large enough cache', {
        windowStart, windowEnd, cacheLength: cache.length
      });
    }

    windowStartIndexRef.current = windowStart;
    setItems(windowItems);

    let newFocusIndex = null;
    let preservedByEventId = false;
    let preservedByIndexMath = false;

    // Method 1: Preserve by event ID first (most reliable)
    const anchorId = preserveFocusEventId || currentFocusEventIdRef.current;

    if (anchorId) {
      const anchor = String(anchorId);
      // Recycled copies deliberately keep the original's event_id, so match the
      // unique render key first — otherwise focus could snap back to an earlier
      // copy of the same item and the rail would appear to jump backwards.
      let foundIndex = windowItems.findIndex((ev) => ev.__renderKey === anchor);
      if (foundIndex < 0) {
        foundIndex = windowItems.findIndex((ev) => {
          const evId = String(ev.id ?? ev.event_id ?? '');
          return evId === anchor;
        });
      }

      if (foundIndex >= 0) {
        newFocusIndex = foundIndex;
        preservedByEventId = true;
        lastShiftPreservedByEventIdRef.current = true; // Part 3: Track this preservation
        console.log('[useFeedBatches] ✅ Preserved focus by EVENT ID:', anchorId, '→ index', foundIndex);
      }
    }

    // Method 2: Fallback to index-based preservation
    if (newFocusIndex === null && preserveFocusIndex !== null && preserveFocusIndex >= 0 && preserveFocusIndex < windowItems.length) {
      newFocusIndex = preserveFocusIndex;
      preservedByIndexMath = true;
      lastShiftPreservedByEventIdRef.current = false; // Part 3: Not preserved by event ID
      console.log('[useFeedBatches] ✅ Preserved focus by INDEX math → index', preserveFocusIndex);
    }

    // Method 3: Final fallback - center the focus
    if (newFocusIndex === null) {
      newFocusIndex = Math.floor(windowItems.length / 2);
      lastShiftPreservedByEventIdRef.current = false; // Part 3: Not preserved by event ID
      console.log('[useFeedBatches] ⚠️ Using fallback center focus at index:', newFocusIndex);
    }

    // Determine if this focus is authoritative (safe to anchor to)
    let isAuthoritativeFocus = false;

    // if we found the same eventId in the new window, that's authoritative
    if (preservedByEventId) {
      isAuthoritativeFocus = true;
    }

    // if we fell back to index math AND the resulting index is inside the displayed window, accept it
    if (!isAuthoritativeFocus && preservedByIndexMath) {
      if (newFocusIndex >= 0 && newFocusIndex < windowItems.length) {
        isAuthoritativeFocus = true;
      }
    }

    // if we ended up with 0 or center because window wasn't ready, leave isAuthoritativeFocus = false
    console.log('[useFeedBatches] Focus quality:', {
      index: newFocusIndex,
      isAuthoritative: isAuthoritativeFocus,
      method: preservedByEventId ? 'eventId' : preservedByIndexMath ? 'indexMath' : 'fallback'
    });

    setFocusIndex(newFocusIndex);
    setFocusIsAuthoritative(isAuthoritativeFocus);
    focusedIndexRef.current = newFocusIndex;
    focusIsAuthoritativeRef.current = isAuthoritativeFocus;

    // Update current focus event ID ref
    if (windowItems[newFocusIndex]) {
      const focused = windowItems[newFocusIndex];
      currentFocusEventIdRef.current =
        focused.__renderKey || String(focused.id ?? focused.event_id ?? '');
    }

    console.log('[useFeedBatches] Window updated:', {
      windowStart,
      windowEnd,
      displayedItems: windowItems.length,
      totalCached: cache.length,
      focusIndex: newFocusIndex,
      focusIsAuthoritative: isAuthoritativeFocus
    });
  }, []);

  /**
   * Initialize feed - IMPROVED: Fetch multiple pages to build a larger initial cache
   * This ensures we have more variety before starting to rotate
   */
  const initialize = useCallback(async () => {
    if (!enabled || isInitialized) return;

    console.log('[useFeedBatches] Initializing feed... (lat:', lat, 'lng:', lng, ')');
    setIsLoadingNext(true);
    setError(null);
    isInRotationModeRef.current = false;

    try {
      const MAX_PREFETCH_PAGES = 5;

      // PHASE 1: Fetch page 1 and show it immediately so users see content fast
      const firstBatch = await fetchEvents(null, BATCH_SIZE, true);

      if (firstBatch.events.length === 0) {
        if (firstBatch.nextCursor === 'wrap_refresh') {
          nextCursorRef.current = 'wrap_refresh';
        }
        // Previously this called setError('No events available'), which made
        // SmartEventList swap the whole list out for a bare error string. An
        // empty result is not a failure — mark the feed initialized with zero
        // items so the list renders its own localized empty state and the user
        // can change area/filters from there.
        eventCacheRef.current = [];
        windowStartIndexRef.current = 0;
        setItems([]);
        setIsInitialized(true);
        setIsLoadingNext(false);
        console.log('[useFeedBatches] ℹ️ Feed initialized empty — showing empty state, not an error');
        return;
      }

      eventCacheRef.current = firstBatch.events;
      nextCursorRef.current = firstBatch.nextCursor;
      windowStartIndexRef.current = 0;

      const windowItems = firstBatch.events.slice(0, Math.min(WINDOW_SIZE, firstBatch.events.length));
      setItems(windowItems);

      const centerIndex = Math.floor(windowItems.length / 2);
      setFocusIndex(centerIndex);
      focusedEventIdRef.current = windowItems[centerIndex]?.event_id;
      currentFocusEventIdRef.current = String(windowItems[centerIndex]?.event_id ?? '');

      setIsInitialized(true);  // show content now — spinner gone
      setIsLoadingNext(false);

      console.log('[useFeedBatches] ✅ Phase 1 done —', windowItems.length, 'items visible. Starting background prefetch...');

      // PHASE 2: Prefetch pages 2–5 in the background (ref mutations only, no re-renders)
      if (!firstBatch.nextCursor || firstBatch.nextCursor === 'wrap_refresh') return;

      let currentCursor = firstBatch.nextCursor;
      let fetchCount = 1;

      while (fetchCount < MAX_PREFETCH_PAGES) {
        const batch = await fetchEvents(currentCursor, BATCH_SIZE, true);

        if (batch.events.length === 0) {
          if (batch.nextCursor === 'wrap_refresh') {
            nextCursorRef.current = 'wrap_refresh';
          }
          console.log('[useFeedBatches] ⚠️ Background prefetch: no more items on page', fetchCount + 1);
          break;
        }

        eventCacheRef.current = [...eventCacheRef.current, ...batch.events];
        nextCursorRef.current = batch.nextCursor;
        currentCursor = batch.nextCursor;
        fetchCount++;

        console.log('[useFeedBatches] 📄 Background prefetch page', fetchCount, '— cache now', eventCacheRef.current.length, 'items');

        if (!currentCursor || currentCursor === 'wrap_refresh') {
          nextCursorRef.current = currentCursor;
          break;
        }
      }

      console.log('[useFeedBatches] ✅ Background prefetch complete —', eventCacheRef.current.length, 'items in cache');

      // Phase 1 only rendered page 1 (≤ BATCH_SIZE items) while the prefetch
      // filled the cache behind it, so the window was left half empty until the
      // first shift happened to widen it. Widen it now instead: the window is
      // still anchored at 0 and this only APPENDS rows below the fold, so no
      // scroll compensation is involved and the user sees nothing move.
      //
      // This matters most when the record pool is small — a short list that
      // barely fills the viewport sits permanently inside the bottom edge zone
      // and asks for data it doesn't need.
      if (windowStartIndexRef.current === 0 && eventCacheRef.current.length > WINDOW_SIZE) {
        updateWindowFromCache(0);
      }
    } catch (err) {
      setError('Failed to load events');
      console.error('[useFeedBatches] Initialization error:', err);
    } finally {
      setIsLoadingNext(false);
    }
  }, [enabled, isInitialized, fetchEvents, lat, lng, startDate, endDate, updateWindowFromCache]);

  /**
   * ENDLESS FEED: append a replayed copy of items we already hold.
   *
   * Last-resort fallback for when the backend is dry — every wrap_refresh has
   * come back with nothing the cache doesn't already contain. Previously the
   * window simply could not shift at that point and the horizontal rail hit a
   * hard stop. Replaying keeps scrolling alive.
   *
   * Recycled entries keep their real event_id (so saving, view counts, map
   * markers and the detail view all still resolve the correct record) and gain
   * a unique `__renderKey` for React keys and client-side dedup, plus
   * `__repeatCycle` so the UI can suppress analytics on repeats.
   *
   * @returns {number} how many items were appended
   */
  const appendRecycledBatch = useCallback((count = BATCH_SIZE) => {
    let cache = eventCacheRef.current;
    const howManyRequested = Math.max(1, count);

    // Keep memory bounded: a rail that replays forever would otherwise grow the
    // cache without limit. Drop the oldest entries (never anything the current
    // window still needs, plus one window of backwards-scroll headroom) and
    // slide the window index to match.
    if (cache.length + howManyRequested > TRIM_THRESHOLD) {
      const maxTrim = Math.max(0, windowStartIndexRef.current - WINDOW_SIZE);
      const trimCount = Math.min(TRIM_AMOUNT, maxTrim);
      if (trimCount > 0) {
        cache = cache.slice(trimCount);
        eventCacheRef.current = cache;
        windowStartIndexRef.current -= trimCount;
        console.log(`[useFeedBatches] 🗑️ Trimmed ${trimCount} old cache entries before replay`);
      }
    }

    // Only ever replay originals, so cycles don't compound into copies of copies.
    // Also dedup by event_id: the "final retry" path in loadNext deliberately
    // admits items the cache already holds (it is the only way to get content
    // from a dry backend), and with a small record pool those duplicates would
    // otherwise make the replay cycle short and repetitive — the same handful
    // of events coming round several times more often than necessary.
    const seenSourceIds = new Set();
    const source = cache.filter((item) => {
      if (item.__repeatCycle) return false;
      const id = String(item.event_id ?? item.id ?? '');
      if (id && seenSourceIds.has(id)) return false;
      if (id) seenSourceIds.add(id);
      return true;
    });
    if (source.length === 0) {
      console.log('[useFeedBatches] ♻️ Nothing to recycle — no original items in cache');
      return 0;
    }

    recycleCycleRef.current += 1;
    const cycle = recycleCycleRef.current;
    const startAt = recycleOffsetRef.current % source.length;
    const howMany = howManyRequested;

    const recycled = [];
    for (let i = 0; i < howMany; i += 1) {
      const position = (startAt + i) % source.length;
      const original = source[position];
      const originalId = String(original.event_id || original.id || position);
      recycled.push({
        ...original,
        __repeatCycle: cycle,
        // `startAt + i` (not `position`) so keys stay unique even when the pool
        // is smaller than the batch and the loop wraps more than once.
        __renderKey: `${originalId}__r${cycle}_${startAt + i}`,
      });
    }

    recycleOffsetRef.current = (startAt + howMany) % source.length;
    eventCacheRef.current = [...cache, ...recycled];

    console.log(
      `[useFeedBatches] ♻️ Pool exhausted — replayed ${recycled.length} item(s) (cycle #${cycle}). Cache now ${eventCacheRef.current.length}`
    );

    return recycled.length;
  }, []);

  /**
   * Load next batch (scroll down)
   * SIMPLIFIED: Sets minimal pendingScrollAdjustment with just what list needs
   * - Fetch new items from API
   * - Add to cache
   * - Shift window forward (remove top items, show next items)
   */
  const loadNext = useCallback(async () => {
    // we might get here from a horizontal edge-demand even if hasNext is false
    if (loadingLockRef.current) {
      console.log(
        "[useFeedBatches] loadNext skipped: loadingLockRef.current=true"
      );
      return;
    }
    if (isLoadingNext) {
      console.log("[useFeedBatches] loadNext skipped: isLoadingNext=true");
      return;
    }
    if (Date.now() < shiftCooldownUntilRef.current) {
      console.log("[useFeedBatches] loadNext skipped: shift cooldown active");
      return;
    }

    loadingLockRef.current = true;
    setIsLoadingNext(true);
    setIsShiftingWindow(true); // Block focus changes during shift
    console.log('[useFeedBatches] 🟣 SHIFT STARTING (loadNext) - window will move forward');

    try {
      // Capture preserved event ID before shift
      const preservedEventId = currentFocusEventIdRef.current || null;

      console.log(
        "[useFeedBatches] 📌 Preserved event ID before shift (may be forced by SmartEventList):",
        preservedEventId
      );

      // CRITICAL FIX: Check if we have enough items in cache before fetching from backend
      const currentCacheSize = eventCacheRef.current.length;
      const currentWindowEnd = windowStartIndexRef.current + WINDOW_SIZE;
      const itemsAfterWindow = currentCacheSize - currentWindowEnd;

      console.log('[useFeedBatches] 📊 Cache status:', {
        cacheSize: currentCacheSize,
        windowStart: windowStartIndexRef.current,
        windowEnd: currentWindowEnd,
        itemsAfterWindow: itemsAfterWindow,
        needsFetch: itemsAfterWindow < BATCH_SIZE
      });

      // Only fetch from backend if we don't have enough items in cache
      if (itemsAfterWindow < BATCH_SIZE && nextCursorRef.current) {
        const isWrapRefresh = nextCursorRef.current === 'wrap_refresh';

        if (isWrapRefresh) {
          console.log('🔄 [useFeedBatches] WRAP REFRESH CYCLE #', wrapCycleCountRef.current + 1, '- Pool exhausted, fetching with reset cursor');
        } else {
          console.log('[useFeedBatches] 🔄 Cache running low, fetching more from backend...');
        }

        const result = await fetchEvents(nextCursorRef.current, BATCH_SIZE);

        if (result.events.length === 0) {
          console.log('[useFeedBatches] ⚠️ Backend returned no more items');

          // INSTAGRAM-STYLE: If pool exhausted, immediately wrap_refresh for infinite scroll
          if (result.nextCursor === 'wrap_refresh') {
            console.log('🔄 [useFeedBatches] Pool exhausted - fetching with wrap_refresh (cycle #' + (wrapCycleCountRef.current + 1) + ')');

            // CRITICAL: Clear most exclusions BEFORE fetching to ensure we get items
            const currentlySeen = Array.from(recentlySeenIdsRef.current);
            console.log('🗑️ [useFeedBatches] Clearing exclusions before wrap (had', currentlySeen.length, 'items)');
            recentlySeenIdsRef.current = new Set(currentlySeen.slice(-5)); // Keep only last 5

            const wrapResult = await fetchEvents('wrap_refresh', BATCH_SIZE);

            if (wrapResult.events.length > 0) {
              // DEDUPLICATION: Filter out items already in cache
              const existingIds = new Set(
                eventCacheRef.current.map(e => String(e.event_id || e.id))
              );
              const uniqueEvents = wrapResult.events.filter(event => {
                const eventId = String(event.event_id || event.id);
                return !existingIds.has(eventId);
              });

              eventCacheRef.current = [...eventCacheRef.current, ...uniqueEvents];
              nextCursorRef.current = wrapResult.nextCursor;
              console.log('✅ [useFeedBatches] Wrap refresh successful! Added', uniqueEvents.length, '/', wrapResult.events.length, 'unique items. Total cache:', eventCacheRef.current.length, 'Wrap cycle:', wrapCycleCountRef.current);

              // If still no unique items after wrap, clear ALL exclusions and try once more
              if (uniqueEvents.length === 0) {
                console.log('⚠️ [useFeedBatches] All items were duplicates - clearing ALL exclusions for final retry');
                recentlySeenIdsRef.current = new Set(); // Clear everything

                const finalRetry = await fetchEvents('wrap_refresh', BATCH_SIZE);
                if (finalRetry.events.length > 0) {
                  eventCacheRef.current = [...eventCacheRef.current, ...finalRetry.events];
                  nextCursorRef.current = finalRetry.nextCursor;
                  console.log('✅ [useFeedBatches] Final retry successful! Added', finalRetry.events.length, 'items');
                }
              }
            } else {
              console.log('❌ [useFeedBatches] Wrap refresh returned 0 items - pool may be exhausted');
              // Don't return - let the window shift continue with what we have
            }
          } else {
            // No wrap refresh available. This used to return early and leave
            // the rail permanently stuck at its last card. Fall through to the
            // recycle guard below instead, which replays existing content.
            console.log('⚠️ [useFeedBatches] No more items and no wrap refresh available — will recycle');
          }
        } else {
          // CLIENT-SIDE DEDUPLICATION: Prevent duplicates in cache
          const existingIds = new Set(
            eventCacheRef.current.map(e => String(e.event_id || e.id))
          );

          const newUniqueEvents = result.events.filter(event => {
            const eventId = String(event.event_id || event.id);
            if (existingIds.has(eventId)) {
              console.warn('[useFeedBatches] 🚫 Skipping duplicate in cache:', eventId);
              return false;
            }
            existingIds.add(eventId); // Add to set for next iteration
            return true;
          });

          // Add only unique events to cache
          eventCacheRef.current = [...eventCacheRef.current, ...newUniqueEvents];
          nextCursorRef.current = result.nextCursor;

          if (isWrapRefresh) {
            console.log('✅ [useFeedBatches] Wrap refresh successful! Added', newUniqueEvents.length, '/', result.events.length, 'unique items. Total cache:', eventCacheRef.current.length, 'Wrap cycle:', wrapCycleCountRef.current);
          } else {
            console.log('[useFeedBatches] Added', newUniqueEvents.length, '/', result.events.length, 'unique items to cache. Total:', eventCacheRef.current.length);
          }

          if (newUniqueEvents.length < result.events.length) {
            console.warn('[useFeedBatches] ⚠️ Filtered', result.events.length - newUniqueEvents.length, 'duplicate items from backend');
          }

          console.log('[useFeedBatches] 📍 Next cursor:', result.nextCursor ? 'Available ✓' : '⚠️ NULL - no more items from backend');
        }
      } else {
        console.log('[useFeedBatches] ✅ Using cached items, no backend fetch needed');
      }

      /**
       * ── Grow the cache BEFORE moving the window ──────────────────────────
       *
       * BUGFIX (window ratchets smaller): the guard here used to be
       * `newWindowStart >= cache.length` — i.e. it only topped the cache up
       * once the new *start* ran clean off the end. That is far too late. With
       * a start of 120 and a cache of 136 the test passed (120 < 136) and no
       * recycling happened, so `updateWindowFromCache` was handed a start it
       * could only satisfy with 16 items. Repeat and the window melts away.
       *
       * The window must never shrink to fit the cache; the cache must grow to
       * fit the window. So the requirement is a full WINDOW_SIZE of items
       * *after* the new start, and any shortfall is replayed up front. The
       * arithmetic that made this unavoidable — advance 15, gain only 8 unique
       * items per wrap cycle — is unchanged; we simply make up the difference
       * from content we already hold instead of letting the window absorb it.
       */
      const desiredStart = windowStartIndexRef.current + BATCH_SIZE;
      const deficit = (desiredStart + WINDOW_SIZE) - eventCacheRef.current.length;

      if (deficit > 0) {
        // Ask for a little headroom (a whole batch) rather than the exact
        // deficit, so we aren't recycling on literally every single shift.
        const added = appendRecycledBatch(Math.max(BATCH_SIZE, deficit));
        if (added === 0) {
          console.log('[useFeedBatches] ⚠️ Cache short by', deficit, 'and nothing available to recycle');
        }
      }

      // appendRecycledBatch may trim the front of the cache, which moves
      // windowStartIndexRef — always recompute from the (possibly updated) ref.
      const maxWindowStart = Math.max(0, eventCacheRef.current.length - WINDOW_SIZE);
      const newWindowStart = Math.max(0, Math.min(windowStartIndexRef.current + BATCH_SIZE, maxWindowStart));
      const actualShift = newWindowStart - windowStartIndexRef.current;

      if (actualShift <= 0) {
        // Genuinely nothing left: the window already sits at the end of a cache
        // that cannot grow. Bail out WITHOUT emitting a scroll adjustment — the
        // old code could announce a 15-item removeTop that never happened,
        // which the list then "compensated" for.
        console.log('[useFeedBatches] ⚠️ Window already at the end of the cache and nothing to recycle — not shifting', {
          windowStart: windowStartIndexRef.current,
          cacheSize: eventCacheRef.current.length,
        });
        loadingLockRef.current = false;
        setIsLoadingNext(false);
        setIsShiftingWindow(false);
        return;
      }

      // Report the shift we ACTUALLY performed, not the one we hoped for.
      // The list's anchor path measures real drift so it self-corrects, but the
      // height-sum fallback trusts this number literally.
      const adjustmentId = ++scrollAdjustmentIdRef.current;
      setPendingScrollAdjustment({
        id: adjustmentId,
        kind: 'removeTop',
        removedCount: actualShift,
        preservedEventId,
        adjustByPx: null, // List will compute px itself
        onApplied: onScrollAdjustmentApplied, // CRITICAL FIX: Pass callback
      });

      // Calculate focus index for window update
      const currentFocusIndex = focusedIndexRef.current;
      const fallbackFocusIndex = Math.max(0, currentFocusIndex - actualShift);

      console.log('[useFeedBatches] 🎯 Fallback focus math: current', currentFocusIndex, '- shift', actualShift, '= new', fallbackFocusIndex);

      // Update window (will try event ID first, then fallback to index).
      // The cache has already been grown to support a full window at this
      // start, so updateWindowFromCache's clamp is a no-op here.
      updateWindowFromCache(newWindowStart, fallbackFocusIndex, preservedEventId);

      console.log('[useFeedBatches] ✅ Shifted window forward to start:', newWindowStart);

    } catch (err) {
      console.error('[useFeedBatches] Load next error:', err);
      setError('Failed to load more items');
    } finally {
      loadingLockRef.current = false;
      shiftCooldownUntilRef.current = Date.now() + SHIFT_COOLDOWN_MS;
      setIsLoadingNext(false);
      setTimeout(() => {
        setIsShiftingWindow(false);
        console.log('[useFeedBatches] 🟢 SHIFT COMPLETED (loadNext) - window shift finished');
      }, 30); // INSTAGRAM-STYLE: Faster unlock (was 80ms) - minimize dead zones
    }
  }, [fetchEvents, isLoadingNext, updateWindowFromCache, appendRecycledBatch]);

  /**
   * Load previous batch (scroll up)
   * SIMPLIFIED: Sets minimal pendingScrollAdjustment with just what list needs
   * - Use cached events (no API call needed)
   * - Shift window backward (show previous items, remove last items)
   */
  const loadPrevious = useCallback(async () => {
    if (loadingLockRef.current || isLoadingPrevious) {
      return;
    }
    if (Date.now() < shiftCooldownUntilRef.current) {
      console.log('[useFeedBatches] loadPrevious skipped: shift cooldown active');
      return;
    }

    const currentWindowStart = windowStartIndexRef.current;

    // Check if there are previous items in cache
    if (currentWindowStart === 0) {
      console.log('[useFeedBatches] Already at the beginning of cache');
      return;
    }

    loadingLockRef.current = true;
    setIsLoadingPrevious(true);
    setIsShiftingWindow(true); // Block focus changes during shift
    console.log('[useFeedBatches] 🟣 SHIFT STARTING (loadPrevious) - window will move backward');

    try {
      console.log('[useFeedBatches] 🔄 Loading previous batch from cache...');

      // Capture preserved event ID before shift
      const preservedEventId = currentFocusEventIdRef.current || null;

      console.log('[useFeedBatches] 📌 Preserved event ID before shift:', preservedEventId);

      // Calculate new window position
      const newWindowStart = Math.max(0, currentWindowStart - BATCH_SIZE);

      // SIMPLIFIED: Send minimal adjustment info - list will compute px itself
      const adjustmentId = ++scrollAdjustmentIdRef.current;
      const prependedCount = currentWindowStart - newWindowStart;
      setPendingScrollAdjustment({
        id: adjustmentId,
        kind: 'prepend',
        prependedCount,
        preservedEventId,
        adjustByPx: null, // List will compute px itself
        onApplied: onScrollAdjustmentApplied, // CRITICAL FIX: Pass callback
      });

      // Calculate new focus index as fallback: currentIndex + shift amount
      const currentFocusIndex = focusedIndexRef.current;
      const fallbackFocusIndex = currentFocusIndex + prependedCount;

      console.log('[useFeedBatches] 🎯 Fallback focus math: current', currentFocusIndex, '+ shift', prependedCount, '= new', fallbackFocusIndex);

      // Update window (will try event ID first, then fallback to index)
      updateWindowFromCache(newWindowStart, fallbackFocusIndex, preservedEventId);

      console.log('[useFeedBatches] ✅ Shifted window backward to start:', newWindowStart);

    } catch (err) {
      console.error('[useFeedBatches] Load previous error:', err);
      setError('Failed to load previous items');
    } finally {
      loadingLockRef.current = false;
      shiftCooldownUntilRef.current = Date.now() + SHIFT_COOLDOWN_MS;
      setIsLoadingPrevious(false);
      setTimeout(() => {
        setIsShiftingWindow(false);
        console.log('[useFeedBatches] 🟢 SHIFT COMPLETED (loadPrevious) - window shift finished');
      }, 30); // INSTAGRAM-STYLE: Faster unlock (was 80ms) - minimize dead zones
    }
  }, [isLoadingPrevious, updateWindowFromCache]);

  /**
   * Handle focus index change from scroll
   * TASK 1: Now accepts both index and eventId
   * ENHANCED: Prevents focus changes during window shifts OR any loading + handles fast scrolling
   */
  const handleFocusChange = useCallback((newIndex, eventId) => {
    // CRITICAL: Ignore ALL focus changes during window shifts OR loading
    if (isShiftingWindow || isLoadingNext || isLoadingPrevious) {
      console.log('[useFeedBatches] 🚫 BLOCKED focus change to index', newIndex, '- shift/load in progress (isShiftingWindow:', isShiftingWindow, 'isLoadingNext:', isLoadingNext, 'isLoadingPrevious:', isLoadingPrevious, ')');
      return;
    }

    setFocusIndex(newIndex);
    const currentEvent = items[newIndex];

    // TASK 1: Store both index and event ID
    focusedIndexRef.current = newIndex;

    // Prefer passed eventId, fallback to deriving from item
    const finalEventId = eventId || currentEvent?.event_id || currentEvent?.id;
    if (finalEventId) {
      currentFocusEventIdRef.current = String(finalEventId);
      focusedEventIdRef.current = String(finalEventId); // Keep old ref for compatibility
    }

    console.log('[useFeedBatches] Focus changed to index:', newIndex, '/', items.length, 'event:', currentEvent?.event_name?.substring(0, 30), 'ID:', finalEventId);

    // BUGFIX (list scrolls up and down by itself): this used to ALSO trigger
    // loadNext()/loadPrevious() whenever the focused index came within 7 of
    // either end of the window. That made it a second, completely independent
    // loading trigger running alongside the lists' `onEdgeDemand` channel — and
    // unlike onEdgeDemand it had no idea whether the user had actually moved.
    //
    // It is an oscillator by construction. The window holds 30 items and a
    // shift moves it by 15, so a shift that fires near one edge lands the
    // preserved item near the *other* edge; the list then reports that new
    // focus, which trips the opposite trigger, which shifts back. The trace
    // shows exactly that ping-pong — index 7 → loadNext → index 25 → loadNext,
    // and 22 → loadPrevious → 7 → loadNext — with the feed burning through
    // wrap_refresh cycles and filtering 22 duplicates a frame.
    //
    // Focus tracking and load triggering are now separated, as
    // SmartEventList's own comment always intended ("Edge-demand channel -
    // separate from focus for reliable loading"). This function records where
    // the user is; the lists decide when more data is needed, and they only do
    // so in response to a real gesture.
  }, [items, isLoadingNext, isLoadingPrevious, isShiftingWindow]);

  /**
   * Handle scroll adjustment applied by list
   * Clears the pending adjustment when list confirms it's done
   */
  const onScrollAdjustmentApplied = useCallback((id) => {
    setPendingScrollAdjustment((prev) => (prev && prev.id === id ? null : prev));
    setIsShiftingWindow(false);
    console.log('[useFeedBatches] ✅ Scroll adjustment', id, 'applied and cleared');
  }, []);

  /**
   * Reset/refresh the feed
   */
  const reset = useCallback(() => {
    setItems([]);
    setFocusIndex(7);
    setIsInitialized(false);
    nextCursorRef.current = null;
    focusedEventIdRef.current = null;
    currentFocusEventIdRef.current = null;
    eventCacheRef.current = [];
    windowStartIndexRef.current = 0;
    seenIdsRef.current = new Set();
    recentlySeenIdsRef.current = new Set(); // Clear recently seen on full reset
    wrapCycleCountRef.current = 0; // Reset wrap counter
    isInRotationModeRef.current = false; // Reset to loading new content mode
    recycleCycleRef.current = 0;  // Fresh criteria → start replaying from scratch
    recycleOffsetRef.current = 0;
    setError(null);
    setPendingScrollAdjustment(null);
  }, []);

  /**
   * Reset and re-initialize the feed (for searchQuery changes)
   */
  const resetAndInitialize = useCallback(async () => {
    reset();
    if (enabled) {
      await initialize();
    }
  }, [reset, initialize, enabled]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    if (enabled && !isInitialized) {
      initialize();
    }
  }, [enabled, isInitialized, initialize]);

  return {
    // State
    items,
    focusIndex,
    focusIsAuthoritative, // NEW: Whether focus was truly preserved (vs weak fallback)
    isLoadingNext,
    isLoadingPrevious,
    isInitialized,
    error,
    isShiftingWindow, // NEW: Expose for UI to prevent interference
    pendingScrollAdjustment, // TASK 2: Expose scroll adjustment info
    wasLastShiftAuthoritative: lastShiftPreservedByEventIdRef.current, // Part 3: Was last shift preserved by event ID

    // Status indicators
    hasNext: !!nextCursorRef.current,
    hasPrevious: windowStartIndexRef.current > 0,

    // Methods
    handleFocusChange, // TASK 1: Now accepts (index, eventId)
    loadNext,
    loadPrevious,
    reset,
    initialize,
    resetAndInitialize,   // NEW: Reset and re-initialize on searchQuery change
    onScrollAdjustmentApplied // NEW: Callback for list to confirm adjustment applied
  };
};
